import { AnswerFormat, EndOfSurveyStepDefinition, FormItem, SurveyStepDefinition } from '../../../types/Survey';
import { evaluatePredicate, ManagedResultsLookup } from '../../../helpers/surveyPredicates';
import { ResultModel, StepResultModel } from '../../../types/SurveyResult';
import { getDefaultSurveyQuestionResult } from './getDefaultSurveyQuestionResult';
import { hasAnswer } from '../../../helpers/surveyResultHelper';

/**
 * Initialise result models for a step's formItems, restoring any previous answers.
 * Shared between FormStep and InstructionStep.
 */
export function initializeFormItemResults(
    formItems: FormItem[],
    managedResults: ManagedResultsLookup,
    stepIdentifier: string,
    presentedDate: string
): ResultModel[] {
    const previousStepResult = managedResults[stepIdentifier];

    return formItems.map(item => {
        const previousQuestionResult = previousStepResult?.QuestionResults?.find(
            r => r.Identifier.toLowerCase() === item.identifier.toLowerCase()
        );

        const getResultType = (): ResultModel['Type'] => {
            if (item.answerFormat?.type === 'FileAnswerFormat') return 'FileResult';
            if (item.answerFormat?.type === 'SignatureAnswerFormat') return 'ConsentSignatureResult';
            return 'QuestionResult';
        };

        const base: ResultModel = {
            ...previousQuestionResult,
            Identifier: item.identifier,
            Type: getResultType(),
            StartDate: presentedDate,
            EndDate: undefined,
        };

        const defaults = getDefaultSurveyQuestionResult(item.identifier, item.answerFormat);
        if (!(base as any).Answers && (defaults as any).Answers !== undefined) {
            (base as any).Answers = (defaults as any).Answers;
        }
        if (!(base as any).Signature && (defaults as any).Signature !== undefined) {
            (base as any).Signature = (defaults as any).Signature;
        }

        return base;
    });
}

/**
 * The step type that governs design-mode form item interaction. An EndOfSurveyStep is
 * presented as its nested renderStep, but the designer's step context resolves to the
 * EndOfSurveyStep itself — so use the renderStep's type when deciding editability.
 */
export function getDesignModeStepType(step: SurveyStepDefinition): string | undefined {
    if (step.type === 'EndOfSurveyStep') {
        return (step as EndOfSurveyStepDefinition).renderStep?.type;
    }
    return step.type;
}

/**
 * Returns true when every step visited so far has been an InstructionStep and
 * the next step exists but is not an InstructionStep — i.e. the survey is about
 * to transition past the intro screens.
 */
export function isLastInstructionStep(
    visitedStepIdentifiers: string[],
    allSteps: SurveyStepDefinition[],
    nextStep: SurveyStepDefinition | null | undefined
): boolean {
    for (const identifier of visitedStepIdentifiers) {
        const step = allSteps.find(s => s.identifier === identifier);
        if (step && step.type !== 'InstructionStep') {
            return false;
        }
    }
    return nextStep != null && nextStep.type !== 'InstructionStep';
}

/**
 * Returns true when this question step should automatically advance after the
 * participant makes a selection (no explicit Next button needed).
 *
 * @param hadPreviousAnswer  Whether the participant had already answered this
 *                           step before (i.e. they are navigating backwards).
 */
export function canAutoContinue(answerFormat: AnswerFormat, hadPreviousAnswer: boolean): boolean {
    const type = answerFormat.type;
    const style = (answerFormat as any).style;
    // SingleButton style always auto-continues (renders the choice as a tappable button)
    if (style === 'SingleButton' && (type === 'TextChoiceAnswerFormat' || type === 'BooleanAnswerFormat')) {
        return true;
    }
    // On a fresh visit (no prior answer, no default), single-choice or boolean auto-continues
    return !hadPreviousAnswer &&
        !(answerFormat as any).defaultValue &&
        ((type === 'TextChoiceAnswerFormat' && style === 'Single') || type === 'BooleanAnswerFormat');
}

/**
 * Assembles a StepResultModel from the step's current results and presentedDate.
 * EndDate is intentionally omitted — the SurveyStep wrapper stamps it at commit time.
 */
export function buildStepResult(
    stepIdentifier: string,
    presentedDate: string,
    questionResults: ResultModel[]
): StepResultModel {
    return {
        Identifier: stepIdentifier,
        StartDate: presentedDate,
        QuestionResults: questionResults,
    };
}

/**
 * Returns the subset of formItems whose hidePredicate (if any) evaluates to
 * false given the current managed results.
 *
 * When ignoreHidePredicates is true (Design previews), all form items are
 * returned unconditionally — no hidePredicate of any type is evaluated. This
 * mirrors Angular's FormStepManager.hideFormItem editMode guard.
 */
export function getVisibleFormItems(
    formItems: FormItem[],
    managedResults: ManagedResultsLookup,
    predicateExpressionEvaluator: (expression: string) => boolean,
    ignoreHidePredicates: boolean = false
): FormItem[] {
    if (ignoreHidePredicates) return formItems;
    return formItems.filter(item => {
        const hidePredicate = item.hidePredicate;
        if (!hidePredicate) return true;
        return !evaluatePredicate(hidePredicate, managedResults, predicateExpressionEvaluator);
    });
}

/**
 * Returns the presentedDate prop if provided, otherwise the current ISO timestamp.
 */
export function resolvePresentedDate(prop?: string): string {
    return prop ?? new Date().toISOString();
}

/**
 * Returns a shallow copy of result with Answers reset to [] and all file/invalid
 * fields removed. Mirrors Angular's StepManager.prototype.skip().
 */
export function clearResultForSkip(result: ResultModel): ResultModel {
    const cleared = { ...result } as any;
    cleared.Answers = [];
    delete cleared.invalid;
    delete cleared.File;
    delete cleared.FileName;
    delete cleared.StorageLocation;
    delete cleared.ContentType;
    delete cleared.Modified;
    return cleared as ResultModel;
}

/**
 * Returns true when the form cannot yet be submitted:
 * - any visible result is marked invalid
 * - any visible required question (has an answerFormat and is not optional) is unanswered
 * - no visible question has been answered at all
 */
export function isContinueDisabled(visibleFormItems: FormItem[], results: ResultModel[]): boolean {
    let anyAnswered = false;
    let anyVisible = false;
    for (const item of visibleFormItems) {
        const result = results.find(r => r.Identifier === item.identifier);
        if (result?.invalid) return true;
        if (item.answerFormat) {
            anyVisible = true;
            if (hasAnswer(result)) {
                anyAnswered = true;
            } else if (!item.optional) {
                return true; // unanswered required question
            }
        }
    }
    return !anyAnswered && anyVisible;
}
